import type { ButtonProps } from "@shared/components/untitledui/base/buttons/button"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import type { DialogProps, HeadingProps, ModalOverlayProps } from "react-aria-components"
import {
  Heading,
  ModalOverlay,
  Modal as ModalPrimitive,
  Button as PrimitiveButton,
  Dialog as PrimitiveDialog,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import IconClose from "../icons/Close"

// Custom Modal component based on IntentUI's, since UntitledUI does not provide one.

// ----------
// Dialog primitives (for use inside modals)
// ----------

const Dialog = ({
  role = "dialog",
  className,
  ...props
}: React.ComponentProps<typeof PrimitiveDialog>) => (
  <PrimitiveDialog
    data-slot="dialog"
    role={role}
    className={twMerge(
      "peer/dialog group/dialog relative flex max-h-[calc(var(--visual-viewport-height)-var(--visual-viewport-vertical-padding))] flex-col overflow-hidden outline-hidden [--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]",
      className,
    )}
    {...props}
  />
)

// ----------
// Modal sub-components
// ----------

export interface ModalHeaderProps extends Omit<React.ComponentProps<"div">, "title"> {}

export const ModalHeader = ({ className, ...props }: ModalHeaderProps) => (
  <div
    data-slot="dialog-header"
    className={twMerge(
      "relative space-y-1 p-(--gutter) pb-[calc(var(--gutter)---spacing(3))]",
      className,
    )}
    {...props}
  />
)

export interface ModalTitleProps extends HeadingProps {
  ref?: React.Ref<HTMLHeadingElement>
}

export const ModalTitle = ({ className, ref, ...props }: ModalTitleProps) => (
  <Heading
    slot="title"
    ref={ref}
    className={twMerge("text-fg text-lg/6 font-semibold text-balance sm:text-base/6", className)}
    {...props}
  />
)

export const ModalDescription = ({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLParagraphElement> } & React.ComponentProps<"p">) => (
  <p
    data-slot="description"
    className={twMerge(
      "text-muted-fg text-base/6 text-pretty group-disabled:opacity-50 sm:text-sm/6",
      className,
    )}
    ref={ref}
    {...props}
  />
)

export const ModalBody = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="dialog-body"
    className={twMerge(
      "isolate flex min-h-0 flex-1 flex-col overflow-auto px-(--gutter) py-1",
      className,
    )}
    {...props}
  />
)

export const ModalFooter = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    data-slot="dialog-footer"
    className={twMerge(
      "isolate mt-auto flex flex-col-reverse justify-end gap-3 p-(--gutter) pt-[calc(var(--gutter)---spacing(2))] group-not-has-data-[slot=dialog-body]/dialog:pt-0 sm:flex-row",
      className,
    )}
    {...props}
  />
)

export const ModalClose = ({ color = "secondary", ...props }: ButtonProps) => (
  <Button slot="close" color={color} {...props} />
)

const DialogCloseIcon = ({
  isDismissable,
  className,
}: {
  isDismissable?: boolean
  className?: string
}) =>
  isDismissable ? (
    <PrimitiveButton
      aria-label="Close"
      slot="close"
      className={twMerge(
        "absolute end-1 top-1 z-50 grid size-8 place-content-center rounded-xl hover:bg-secondary focus:bg-secondary focus:outline-hidden focus-visible:ring-1 focus-visible:ring-primary sm:end-3 sm:top-3 sm:size-9 sm:rounded-md",
        className,
      )}
    >
      <IconClose className="size-4" />
    </PrimitiveButton>
  ) : null

// ----------
// ModalContent (controlled modal overlay)
// ----------

const sizes = {
  "2xs": "sm:max-w-2xs",
  xs: "sm:max-w-xs",
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  "5xl": "sm:max-w-5xl",
  fullscreen: "",
}

interface ModalContentProps
  extends
    Omit<ModalOverlayProps, "children" | "className">,
    Pick<DialogProps, "aria-label" | "aria-labelledby" | "role" | "children"> {
  className?: string
  size?: keyof typeof sizes
  closeButton?: boolean
  overlay?: { className?: string }
}

export const ModalContent = ({
  className,
  isDismissable: isDismissableInternal,
  children,
  overlay,
  size = "md",
  role = "dialog",
  closeButton = true,
  ...props
}: ModalContentProps) => {
  const isDismissable = isDismissableInternal ?? role !== "alertdialog"
  return (
    <ModalOverlay
      data-slot="modal-overlay"
      isDismissable={isDismissable}
      className={twMerge(
        "fixed start-0 top-0 z-50 h-(--visual-viewport-height,100vh) w-screen",
        "bg-bg/15 backdrop-blur-[1px] motion-reduce:backdrop-blur-none",
        "grid grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr]",
        "entering:duration-300 entering:ease-out entering:animate-in entering:fade-in",
        "exiting:ease-in exiting:animate-out exiting:fade-out",
        size === "fullscreen" ? "md:p-3" : "md:p-4",
        overlay?.className,
      )}
      {...props}
    >
      <ModalPrimitive
        data-slot="modal-content"
        className={twMerge(
          "row-start-2 w-full text-start align-middle",
          "[--visual-viewport-vertical-padding:16px]",
          size === "fullscreen"
            ? "sm:[--visual-viewport-vertical-padding:16px]"
            : "sm:[--visual-viewport-vertical-padding:32px]",
          "text-overlay-fg relative overflow-hidden bg-primary",
          "rounded-t-2xl ring inset-shadow-xs ring-secondary_alt drop-shadow-xl sm:rounded-2xl",
          sizes[size],
          "entering:duration-300 entering:ease-out entering:animate-in entering:slide-in-from-bottom sm:entering:slide-in-from-bottom-0 sm:entering:zoom-in-95",
          "exiting:ease-in exiting:animate-out exiting:slide-out-to-bottom sm:exiting:slide-out-to-bottom-0 sm:exiting:zoom-out-95",
          className,
        )}
      >
        <Dialog role={role}>
          {(values) => (
            <>
              {typeof children === "function" ? children(values) : children}
              {closeButton && <DialogCloseIcon isDismissable={isDismissable} />}
            </>
          )}
        </Dialog>
      </ModalPrimitive>
    </ModalOverlay>
  )
}
